import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import Button from 'components/commercetools-ui/atoms/button';
import Typography from 'components/commercetools-ui/atoms/typography';
import FlagIcons from 'components/icons/flags';
import { MarketContext } from 'context/market';
import useMobileDevice from 'helpers/hooks/useMobileDevice';
import { useCart } from 'frontastic';
import MarketModal from './components/market-modal';
import getCurrency from './helpers/getCurrency';
import RedirectToProperDomain from './helpers/redirectToProperDomain';
import { euCountries } from './helpers/sortCountries';
import { Market } from '../header/types';
import { EU_DOMAINS } from 'helpers/constants/domains';
export interface Props {
  menuTop?: boolean;
  onClickHandler?: () => void;
}

const MarketButton: FC<Props> = ({ onClickHandler }) => {
  const [showMarket, setShowMarket] = useState(false);
  const { market: selectedMarket, markets, handleMarket } = useContext(MarketContext);
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session', 'guest-session']);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const { data, cartRecalculate } = useCart();
  const [isLoading, setIsLoading] = useState(false);

  const [currencyOptions, setCurrencyOptions] = useState([
    { currency: 'GBP', currencyCode: '£' },
    { currency: 'EUR', currencyCode: '€' },
  ]);
  const { isMobileDevice } = useMobileDevice();

  useEffect(() => {
    const currentHostname = window.location.hostname;

    if (cookies.locale_selection) {
      setSelectedCountry(cookies.locale_selection.countryCode);
      setSelectedCurrency(cookies.locale_selection.currency);
    } else {
      setSelectedCountry('GB');
      setSelectedCurrency(EU_DOMAINS?.includes(currentHostname) ? 'EUR' : 'GBP');
    }
  }, [cookies.locale_selection]);

  const handleMarketClick = async () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    setCookie('locale_selection', JSON.stringify({ countryCode: selectedCountry, currency: selectedCurrency }), {
      expires: tomorrow,
    });

    const chosenMarket = markets.find((market) => market.flag === selectedCountry);
    handleMarket(chosenMarket as Market);
    setShowMarket(false);

    const redirectToProperDomain = RedirectToProperDomain(selectedCountry, cookies);
    let url = redirectToProperDomain(selectedCurrency);

    if (url) {
      if (data?.cartId) {
        await cartRecalculate();
      }
      setTimeout(() => {
        const frontasticSession = cookies['frontastic-session'];
        const newFrontasticSession = getCookieValue('frontastic-session') || '';
        if (newFrontasticSession && frontasticSession !== newFrontasticSession) {
          url = url && url.replace(frontasticSession, newFrontasticSession);
        }
        //Delele the cookies before the redirect
        const guestSession = cookies['guest-session'];
        if (guestSession) {
          setCookie('guest-session', '', { path: '/', maxAge: 0 });
        }
        document.cookie = 'locale_selection=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = 'frontastic-session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

        window.location.replace(url || '');
        setIsLoading(false);
      }, 3000);
    } else {
      setIsLoading(false);
      console.error('Redirect URL could not be determined.');
    }
  };

  const getCookieValue = (cookieName: string) => {
    const name = cookieName + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');

    for (let i = 0; i < cookieArray.length; i++) {
      const cookie = cookieArray[i].trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length, cookie.length);
      }
    }
    return null;
  };

  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
    const euCountryCodes = euCountries();
    if (euCountryCodes.includes(e.target.value)) {
      setSelectedCurrency('EUR');
    } else {
      setSelectedCurrency('GBP');
    }
  };

  const handleCurrencyChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedCurrency(e.target.value);
  };

  return (
    <div
      className={`w-fit justify-center lg:flex ${isMobileDevice ? '-ml-10' : ''}`}
      onClick={() => isMobileDevice && onClickHandler && onClickHandler()}
    >
      {selectedMarket && (
        <Button
          variant="ghost"
          size="fit"
          onClick={() => {
            setShowMarket(true);
            setIsLoading(false);
          }}
          className={`flex w-fit items-center ${isMobileDevice ? 'gap-5' : ''}`}
        >
          <FlagIcons flagName={selectedCountry} className="mr-3 h-14 w-24" />
          <Typography
            className={`mt-2  ${
              !isMobileDevice ? 'text-14 font-light text-black' : 'text-16 font-medium text-primary-black'
            }`}
          >
            {isMobileDevice
              ? `Change country / currency (${getCurrency(selectedCurrency)})`
              : `(${getCurrency(selectedCurrency)})`}
          </Typography>
        </Button>
      )}
      <MarketModal
        showMarket={showMarket}
        hideMarketMenu={() => setShowMarket(false)}
        selectedCountry={selectedCountry}
        selectedCurrency={selectedCurrency}
        handleCountryChange={handleCountryChange}
        handleCurrencyChange={handleCurrencyChange}
        handleMarketClick={handleMarketClick}
        currencyOptions={currencyOptions}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    </div>
  );
};

export default MarketButton;
